import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import ProjectHub from "../pages/ProjectHub";
import SiteInspection from "../pages/SiteInspection";
import SiteSurvey from "../pages/SiteSurvey";
import { CrmDetailDrawer } from "./crm/CrmDetailDrawer";
import { drawerStackOrder } from "./crm/GlobalLeadDrawer";

/**
 * GlobalProjectDrawer —— 全局「项目详情」抽屉（挂一次，全站可用）
 *
 * 需求来源（2026-10-15）：用户要求把「项目详情」（/project/:id，ProjectHub 整页）
 * 改为抽屉式，参考「已签约客户详情」的做法。
 *
 * 做法与 GlobalLeadDrawer（客户线索）完全同构，两层叠加：
 *   ① history 劫持：站内任何页面点项目（全站 20+ 处入口：Dashboard / 项目地图 /
 *      完工存档 / 看板 / 全局搜索 / 聊天跳转 / CRM 详情 / 完工报告…），导航到
 *      /project/<id> 时改写成「来源页 + ?project=<id>」，抽屉就地弹出，
 *      来源页、面包屑、菜单高亮全部不动，背景页不卸载；
 *   ② 兜底：直链 / 刷新 / 新标签打开 /project/<id>（路径形态）时，replace 到
 *      「上一个非详情页 ?project=<id>」；没有上一个页（刷新场景）则落到
 *      项目地图 /projects/map 当背景 —— 与 CRM「线索详情直链 → 客户看板」对应。
 *
 * URL 契约：
 *   · `?project=<id>`                —— 第 0 层：项目详情抽屉（可挂任意路由上）
 *   · `?project=<id>&inspect=1`      —— 第 1 层：现场巡检抽屉（叠在项目详情上）
 *   · `?project=<id>&survey=1`       —— 第 1 层：现场勘测抽屉（?page= 分页参数照常传递）
 *   · `/project/<id>` 等路径形态      —— 只保留给直链 / 分享，进入后即被兜底改写
 *
 * 多级抽屉（需求 2026-10-15 用户拍板）：项目内子页面用多级抽屉展示，参考已签约
 * 客户详情里点「原线索详情」的叠层方式 —— 第 1 层更窄 70px、z 高一级，项目抽屉不关。
 *
 * ⚠️ 拦截点是 history.pushState / replaceState（React Router 所有跳转最终都走它），
 *    而不是「先跳详情路由再在 effect 里改回来」——后者会让背景页先卸载再重挂，
 *    触发背景页自己的 URL 同步 effect 把刚写的参数冲掉（GlobalLeadDrawer 里实测过）。
 */

// 详情在 URL 里的承载参数（查询参数形态）
export const PROJECT_PARAM = "project";
// 子路由抽屉参数：现场巡检 / 现场勘勘
export const INSPECT_PARAM = "inspect";
export const SURVEY_PARAM = "survey";
// 路径形态：/project/<id>[/inspection|/site-survey]（可带 query / hash）
const DETAIL_URL = /^\/project\/([^/?#]+)(?:\/(inspection|site-survey))?(?:[?#].*)?$/;
// 直链/刷新且没有「上一个非详情页」时的兜底背景页：项目地图（项目域主列表）
const FALLBACK_BG = "/projects/map";
// 「本身就是一个详情抽屉」的路由。命中说明底下已经有一层抽屉（已签约客户详情 / 客户线索详情），
// 本抽屉就要作为第 1 层叠上去（更窄 70px + z 更高，见 CrmDetailDrawer 的 level 参数）。
// ⚠️ 以后新增别的详情抽屉路由，记得加进这里，否则会出现「新抽屉被压在下面看不见」。
const STACKED_BASE_PATHS = [/^\/crm\/signed\/[^/]+$/, /^\/crm\/leads\/[^/]+$/];
// 项目详情自带的深链参数（只对详情有意义）：关抽屉时一并摘掉，避免污染来源页 URL
// （对齐 CRMSignedCustomers 关闭时丢 collect 参数的做法）。
// 暂停审批提醒用 pause=1 展开面板，关闭项目时一起清理，避免影响下一个项目。
const DETAIL_ONLY_PARAMS = ["task", "sub", "section", "rate", "ms", "pause"];

export const GlobalProjectDrawer = () => {
  const { lang } = useApp();
  const zh = lang === "zh";
  const location = useLocation();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  // 「上一个非详情页面」—— 仅作为兜底路径使用（主路径已被 history 拦截处理）
  const bgRef = useRef(null);

  // ── 主拦截：在导航真正发生之前改写 URL，背景页不卸载 ─────────────────────────
  // 拦截范围严格限定：只改「形如 /project/<id>[/inspection|/site-survey]」的目标；
  // 其余导航原样透传。目标 URL 自带的 query（?sub= / ?section= / ?task= / ?page= 等）
  // 会搬到来源页上，深链行为与整页态一致。
  useEffect(() => {
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    const rewrite = (url) => {
      if (typeof url !== "string") return url;                        // 不传 url = 只改 state，不动
      let pathname = url, search = "", hash = "";
      try { const u = new URL(url, window.location.origin); pathname = u.pathname; search = u.search; hash = u.hash; }
      catch { return url; }
      const m = DETAIL_URL.exec(pathname);
      if (!m) return url;                                             // 不是项目详情路由，不动
      const next = new URLSearchParams(window.location.search);
      const own = new URLSearchParams(search);                        // 目标 URL 自带参数优先，覆盖来源页同名参数
      own.forEach((v, k) => next.set(k, v));
      next.set(PROJECT_PARAM, m[1]);
      if (m[2] === "inspection") next.set(INSPECT_PARAM, "1");
      if (m[2] === "site-survey") next.set(SURVEY_PARAM, "1");
      return `${window.location.pathname}?${next.toString()}${hash}`;
    };
    // 防重：改写结果与当前 URL 完全相同时（抽屉里再点同一个项目的同一子页），
    // 把 push 降级成 replace —— 否则 React Router 仍会压入一条重复历史记录，
    // 用户按「后退」要多按一次才离开抽屉。
    const wrap = (orig) => function (state, title, url) {
      const next = rewrite(url);
      const same = next === `${window.location.pathname}${window.location.search}${window.location.hash}`;
      return (same ? origReplace : orig).call(this, state, title, same ? null : next);
    };
    window.history.pushState = wrap(origPush);
    window.history.replaceState = wrap(origReplace);
    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []); // eslint-disable-line

  // ── 兜底：万一某次导航绕过了上面的拦截（直链 / 刷新 / 新标签），仍能把 URL 纠正回来 ──
  // 代价是背景页会重挂一次，所以只作保险，不作主路径。
  const pathMatch = DETAIL_URL.exec(location.pathname);
  const pathSub = pathMatch ? pathMatch[2] || "" : "";

  useLayoutEffect(() => {
    if (!pathMatch) {
      bgRef.current = { pathname: location.pathname, search: location.search };
      return;
    }
    // 兜底改写：保留来源页参数（bg）+ 路径形态自带的 query（如勘测的 ?page= 分页参数），
    // 否则直链 /project/<id>/site-survey?page=site 会丢掉 page、落回总览页。
    const bg = bgRef.current;
    const q = new URLSearchParams(location.search);
    const bgQ = new URLSearchParams(bg?.search || "");
    bgQ.forEach((v, k) => { if (!q.has(k)) q.set(k, v); });
    q.set(PROJECT_PARAM, pathMatch[1]);
    if (pathSub === "inspection") q.set(INSPECT_PARAM, "1");
    if (pathSub === "site-survey") q.set(SURVEY_PARAM, "1");
    nav(`${bg ? bg.pathname : FALLBACK_BG}?${q.toString()}${location.hash || ""}`, { replace: true });
  }, [location.pathname, location.search, pathMatch, pathSub, nav]); // eslint-disable-line

  const projectId = sp.get(PROJECT_PARAM) || "";
  const inspect = sp.get(INSPECT_PARAM) === "1";
  const survey = sp.get(SURVEY_PARAM) === "1";

  // 关闭第 1 层子抽屉：只摘 inspect / survey（+ survey 的 page 分页参数），项目抽屉留着
  const closeSub = (key, extra) => {
    const n = new URLSearchParams(sp);
    n.delete(key);
    if (key === SURVEY_PARAM) n.delete("page");
    if (extra) Object.entries(extra).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    nav(`${location.pathname}?${n.toString()}`, { replace: true });
  };
  // 巡检抽屉返回：回到项目详情（对齐整页态 si-back → /project/:id 的行为）
  const closeInspect = () => closeSub(INSPECT_PARAM);
  // 勘测抽屉返回：回到项目详情并定位到「现场踏勘采集」任务
  // （对齐整页态 survey-back → /project/:id?task=WF-01-02-01 的行为，task 由 ProjectHub 读取）
  const closeSurvey = () => closeSub(SURVEY_PARAM, { task: "WF-01-02-01" });

  // 关闭第 0 层项目抽屉：摘掉 project 与所有只对详情有意义的参数，来源页原样停留
  const closeProject = () => {
    const n = new URLSearchParams(sp);
    n.delete(PROJECT_PARAM);
    n.delete(INSPECT_PARAM);
    n.delete(SURVEY_PARAM);
    n.delete("page");
    DETAIL_ONLY_PARAMS.forEach((k) => n.delete(k));
    nav(`${location.pathname}${n.toString() ? `?${n.toString()}` : ""}`, { replace: true });
  };

  if (!projectId) return null;

  // 抽屉层级 = 页面自带抽屉（STACKED_BASE_PATHS 命中，占第 0 层）+ 本参数在 URL 里的出现顺序。
  // 参数顺序即打开顺序 → 后打开的叠在上面（详见 GlobalLeadDrawer 里 drawerStackOrder 的说明）。
  // 子页面抽屉（巡检 / 勘测）再 +1。必须始终 < 50（Radix 弹窗层级）。
  const baseLevel = (STACKED_BASE_PATHS.some((re) => re.test(location.pathname)) ? 1 : 0)
    + Math.max(0, drawerStackOrder(location.search).indexOf(PROJECT_PARAM));
  const subLevel = baseLevel + 1;

  return (
    <>
      {/* 项目信息整体吸顶后复用客户详情的 flushTop：移除滚动容器顶部留白，避免吸顶栏上方露出滚动内容。 */}
      <CrmDetailDrawer title={zh ? "项目详情" : "Project detail"} onClose={closeProject} testId="project-detail-drawer" level={baseLevel} flushTop>
        <ProjectHub embedded projectId={projectId} onClose={closeProject} />
      </CrmDetailDrawer>
      {inspect && (
        <CrmDetailDrawer title={zh ? "现场巡检" : "Site inspection"} onClose={closeInspect} testId="project-inspect-drawer" level={subLevel}>
          <SiteInspection embedded projectId={projectId} onClose={closeInspect} />
        </CrmDetailDrawer>
      )}
      {survey && (
        <CrmDetailDrawer title={zh ? "现场勘测" : "Site survey"} onClose={closeSurvey} testId="project-survey-drawer" level={subLevel}>
          <SiteSurvey embedded projectId={projectId} onClose={closeSurvey} />
        </CrmDetailDrawer>
      )}
    </>
  );
};

export default GlobalProjectDrawer;
